import * as THREE from 'three';

/** Worn-brick chamfer, meters. Kept below the mortar rim so siege joints still map. */
export const BRICK_BEVEL_M = 0.0028;

type Vec3 = [number, number, number];

function pushUv(uvs: number[], p: Vec3, nx: number, ny: number, nz: number, hw: number, hh: number, hd: number): void {
  const ax = Math.abs(nx);
  const ay = Math.abs(ny);
  const az = Math.abs(nz);
  if (ax >= ay && ax >= az) {
    uvs.push(p[2] / (hd * 2) + 0.5, p[1] / (hh * 2) + 0.5);
  } else if (ay >= az) {
    uvs.push(p[0] / (hw * 2) + 0.5, p[2] / (hd * 2) + 0.5);
  } else {
    uvs.push(p[0] / (hw * 2) + 0.5, p[1] / (hh * 2) + 0.5);
  }
}

function outwardTri(
  positions: number[],
  normals: number[],
  uvs: number[],
  a: Vec3,
  b: Vec3,
  c: Vec3,
  hw: number,
  hh: number,
  hd: number,
): void {
  const e1x = b[0] - a[0];
  const e1y = b[1] - a[1];
  const e1z = b[2] - a[2];
  const e2x = c[0] - a[0];
  const e2y = c[1] - a[1];
  const e2z = c[2] - a[2];
  let nx = e1y * e2z - e1z * e2y;
  let ny = e1z * e2x - e1x * e2z;
  let nz = e1x * e2y - e1y * e2x;
  const cx = (a[0] + b[0] + c[0]) / 3;
  const cy = (a[1] + b[1] + c[1]) / 3;
  const cz = (a[2] + b[2] + c[2]) / 3;
  if (nx * cx + ny * cy + nz * cz < 0) {
    const tmp = b;
    b = c;
    c = tmp;
    nx = -nx;
    ny = -ny;
    nz = -nz;
  }
  const len = Math.hypot(nx, ny, nz) || 1;
  nx /= len;
  ny /= len;
  nz /= len;
  positions.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]);
  normals.push(nx, ny, nz, nx, ny, nz, nx, ny, nz);
  pushUv(uvs, a, nx, ny, nz, hw, hh, hd);
  pushUv(uvs, b, nx, ny, nz, hw, hh, hd);
  pushUv(uvs, c, nx, ny, nz, hw, hh, hd);
}

function quad(
  positions: number[],
  normals: number[],
  uvs: number[],
  a: Vec3,
  b: Vec3,
  c: Vec3,
  d: Vec3,
  hw: number,
  hh: number,
  hd: number,
): void {
  outwardTri(positions, normals, uvs, a, b, c, hw, hh, hd);
  outwardTri(positions, normals, uvs, a, c, d, hw, hh, hd);
}

/**
 * Shared chamfered brick box. Instancing is unchanged: one geometry, local
 * positions still span the original AABB so mortar/atlas `uHalf` mapping holds.
 */
export function createChamferedBoxGeometry(
  width: number,
  height: number,
  depth: number,
  bevel: number = BRICK_BEVEL_M,
): THREE.BufferGeometry {
  const hw = width * 0.5;
  const hh = height * 0.5;
  const hd = depth * 0.5;
  const b = Math.min(Math.max(bevel, 0), hw * 0.28, hh * 0.28, hd * 0.28);
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];

  if (b < 1e-5) {
    const box = new THREE.BoxGeometry(width, height, depth);
    return box;
  }

  const ix = hw - b;
  const iy = hh - b;
  const iz = hd - b;

  // Inset faces (planar, same as a box face pulled in by `b` on the rim).
  quad(positions, normals, uvs, [-ix, -iy, hd], [ix, -iy, hd], [ix, iy, hd], [-ix, iy, hd], hw, hh, hd);
  quad(positions, normals, uvs, [ix, -iy, -hd], [-ix, -iy, -hd], [-ix, iy, -hd], [ix, iy, -hd], hw, hh, hd);
  quad(positions, normals, uvs, [hw, -iy, iz], [hw, -iy, -iz], [hw, iy, -iz], [hw, iy, iz], hw, hh, hd);
  quad(positions, normals, uvs, [-hw, -iy, -iz], [-hw, -iy, iz], [-hw, iy, iz], [-hw, iy, -iz], hw, hh, hd);
  quad(positions, normals, uvs, [-ix, hh, iz], [ix, hh, iz], [ix, hh, -iz], [-ix, hh, -iz], hw, hh, hd);
  quad(positions, normals, uvs, [-ix, -hh, -iz], [ix, -hh, -iz], [ix, -hh, iz], [-ix, -hh, iz], hw, hh, hd);

  // 12 edge chamfers.
  // +Z+X, +Z-X, -Z+X, -Z-X (running Y)
  quad(positions, normals, uvs, [ix, -iy, hd], [hw, -iy, iz], [hw, iy, iz], [ix, iy, hd], hw, hh, hd);
  quad(positions, normals, uvs, [-hw, -iy, iz], [-ix, -iy, hd], [-ix, iy, hd], [-hw, iy, iz], hw, hh, hd);
  quad(positions, normals, uvs, [hw, -iy, -iz], [ix, -iy, -hd], [ix, iy, -hd], [hw, iy, -iz], hw, hh, hd);
  quad(positions, normals, uvs, [-ix, -iy, -hd], [-hw, -iy, -iz], [-hw, iy, -iz], [-ix, iy, -hd], hw, hh, hd);
  // +Z+Y, +Z-Y, -Z+Y, -Z-Y (running X)
  quad(positions, normals, uvs, [-ix, iy, hd], [ix, iy, hd], [ix, hh, iz], [-ix, hh, iz], hw, hh, hd);
  quad(positions, normals, uvs, [ix, -iy, hd], [-ix, -iy, hd], [-ix, -hh, iz], [ix, -hh, iz], hw, hh, hd);
  quad(positions, normals, uvs, [ix, iy, -hd], [-ix, iy, -hd], [-ix, hh, -iz], [ix, hh, -iz], hw, hh, hd);
  quad(positions, normals, uvs, [-ix, -iy, -hd], [ix, -iy, -hd], [ix, -hh, -iz], [-ix, -hh, -iz], hw, hh, hd);
  // +X+Y, +X-Y, -X+Y, -X-Y (running Z)
  quad(positions, normals, uvs, [hw, iy, iz], [hw, iy, -iz], [ix, hh, -iz], [ix, hh, iz], hw, hh, hd);
  quad(positions, normals, uvs, [hw, -iy, -iz], [hw, -iy, iz], [ix, -hh, iz], [ix, -hh, -iz], hw, hh, hd);
  quad(positions, normals, uvs, [-hw, iy, -iz], [-hw, iy, iz], [-ix, hh, iz], [-ix, hh, -iz], hw, hh, hd);
  quad(positions, normals, uvs, [-hw, -iy, iz], [-hw, -iy, -iz], [-ix, -hh, -iz], [-ix, -hh, iz], hw, hh, hd);

  // 8 corner triangles.
  const corners: Array<[Vec3, Vec3, Vec3]> = [
    [[ix, iy, hd], [hw, iy, iz], [ix, hh, iz]],
    [[-ix, iy, hd], [-ix, hh, iz], [-hw, iy, iz]],
    [[ix, -iy, hd], [ix, -hh, iz], [hw, -iy, iz]],
    [[-ix, -iy, hd], [-hw, -iy, iz], [-ix, -hh, iz]],
    [[ix, iy, -hd], [ix, hh, -iz], [hw, iy, -iz]],
    [[-ix, iy, -hd], [-hw, iy, -iz], [-ix, hh, -iz]],
    [[ix, -iy, -hd], [hw, -iy, -iz], [ix, -hh, -iz]],
    [[-ix, -iy, -hd], [-ix, -hh, -iz], [-hw, -iy, -iz]],
  ];
  for (const [a, b, c] of corners) {
    outwardTri(positions, normals, uvs, a, b, c, hw, hh, hd);
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geom.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geom.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geom.computeBoundingBox();
  geom.computeBoundingSphere();
  return geom;
}
